/**
 * SessionSecurityAlert Component
 * Displays warnings and alerts for account sharing detection
 */

"use client";

import React from "react";
import useSessionManagement from "@/hooks/useSessionManagement";
import { AlertTriangle, AlertCircle, X } from "lucide-react";
import { useState } from "react";

interface SessionSecurityAlertProps {
  dismissible?: boolean;
  variant?: "warning" | "alert";
}

export const SessionSecurityAlert = ({
  dismissible = true,
  variant = "warning",
}: SessionSecurityAlertProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { sharingDetected, anomalies, activeSessions } = useSessionManagement({
    autoCheck: true,
    checkInterval: 10 * 60 * 1000, // 10 minutes
  });

  if (isDismissed || !sharingDetected || anomalies.length === 0) {
    return null;
  }

  const isHighRisk = anomalies.length >= 3 || activeSessions.length > 2;
  const bgColor = isHighRisk
    ? "bg-red-50 dark:bg-red-900/20"
    : "bg-yellow-50 dark:bg-yellow-900/20";
  const borderColor = isHighRisk
    ? "border-red-200 dark:border-red-800"
    : "border-yellow-200 dark:border-yellow-800";
  const textColor = isHighRisk
    ? "text-red-900 dark:text-red-100"
    : "text-yellow-900 dark:text-yellow-100";
  const IconComponent = isHighRisk ? AlertTriangle : AlertCircle;

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4 mb-6`}>
      <div className="flex items-start gap-3">
        <IconComponent className={`w-5 h-5 ${textColor} mt-0.5`} />
        <div className="flex-1">
          <h3 className={`font-semibold ${textColor} mb-2`}>
            {isHighRisk ? "تحذير أمني عاجل" : "تنبيه أمني"}
          </h3>
          <p className={`text-sm ${textColor} mb-3`}>
            {isHighRisk
              ? "تم اكتشاف نشاط مريب على حسابك. يرجى المراجعة الفورية."
              : "تم اكتشاف بعض الأنشطة غير العادية. فرجى قراءة التفاصيل أدناه."}
          </p>

          {/* Anomalies List */}
          <ul className={`space-y-1 text-sm ${textColor} mb-3`}>
            {anomalies.map((anomaly: string, index: number) => (
              <li key={index} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-current rounded-full"></span>
                {anomaly}
              </li>
            ))}
          </ul>

          {/* Session Count */}
          {activeSessions.length > 1 && (
            <p className={`text-sm ${textColor} mb-3`}>
              عدد الجلسات النشطة: <strong>{activeSessions.length}</strong>
            </p>
          )}

          {/* Action Button */}
          <a
            href="/dashboard/sessions"
            className={`inline-block px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              isHighRisk
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-yellow-600 hover:bg-yellow-700 text-white"
            }`}
          >
            إدارة الجلسات
          </a>
        </div>

        {/* Close Button */}
        {dismissible && (
          <button
            onClick={() => setIsDismissed(true)}
            className={`p-1 hover:bg-white/20 rounded-lg transition-colors`}
            aria-label="إغلاق"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SessionSecurityAlert;
